import React, { useEffect, useRef, useState } from 'react'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { Link } from 'react-router-dom'
import { fetchAnnounceDetails } from '../../services/announces'
import { fetchUsers } from '../../services/fetchAllUser'
import { API_BASE_URL, X_API_KEY } from '../../services/api'
import { User } from '../interfaces/user'
import { useTranslation } from 'react-i18next'
import { viewAnnounce } from '../../services/viewAnnounce'
import { getUserGender } from '../../services/profileService'
import {
  formatGender,
  formatMultipleStatuses,
  formatURL,
} from '../fonctions/formatGenderStatusUrl'
import { fetchAllProfiles } from '../../services/profileService'
import ImageWithApiKey from './ImageWithApiKey'

const sliderStyles = `
  .slick-track {
    display: flex !important;
    align-items: stretch !important;
  }
  
  .slick-slide {
    height: auto !important;
    margin: 0 5px;
  }
  
  .slick-slide > div {
    height: 100%;
    padding: 0 5px;
  }
  
  @media (min-width: 768px) {
    .slick-slide > div {
      padding: 0 10px;
    }
  }
  
  .slide-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    transition: transform 0.3s ease;
  }
  
  .profile-image-container {
    width: 100%;
    padding-bottom: 100%;
    position: relative;
    border-radius: 50%;
    overflow: hidden;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  
  .profile-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  .profile-image:hover {
    transform: scale(1.05);
  }
  
  .slider-container {
    max-width: 1920px;
    margin: 0 auto;
    padding: 0 0px;
  }
  
  @media (min-width: 768px) {
    .slider-container {
      padding: 0 20px;
    }
  }
  
  .info-container {
    margin-top: 15px;
    padding: 8px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  @media (min-width: 768px) {
    .info-container {
      margin-top: 20px;
      padding: 10px;
    }
  }

  .slick-prev, .slick-next {
    z-index: 1 !important;
    width: 30px;
    height: 30px;
  }
  
  @media (min-width: 1024px) {
    .slick-prev {
      left: -15px;
    }
    .slick-next {
      right: -15px;
    }
  }

  @media (max-width: 640px) {
    .slick-prev, .slick-next {
      display: none !important;
    }
  }
`

const defaultAnnounce = {
  id: 'default',
  galleryUrl: '/images/silouhette-female.jpg',
  isDefault: true,
  user: {
    nickname: '',
    address: {
      city: '',
    },
  },
}

function PremiumSlider() {
  const [announces, setAnnounces] = useState<any[]>([])
  const [, setUsers] = useState<User[]>([])
  const [, setProfiles] = useState<any[]>([])
  const { t } = useTranslation()
  const usersFetched = useRef(false)
  const shuffleArray = (array: any[]) => {
    return array.sort(() => Math.random() - 0.5)
  }

  useEffect(() => {
    const fetchAllData = async () => {
      if (usersFetched.current) return
      usersFetched.current = true

      try {
        const userData = await fetchUsers()
        setUsers(userData)

        const profileData = await fetchAllProfiles()
        setProfiles(profileData)

        const response = await fetch(
          `${API_BASE_URL}/announces?limit=30&sort=createdAt&order=desc`,
          {
            headers: {
              'x-api-key': X_API_KEY,
            },
          },
        )

        if (!response.ok) {
          throw new Error('Failed to fetch announces')
        }

        let data = await response.json()

        data.sort(
          (a: { createdAt: string }, b: { createdAt: string }) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )

        const MIN_ANNOUNCES = 7
        if (data.length < MIN_ANNOUNCES) {
          const defaultAnnounces = Array(MIN_ANNOUNCES - data.length).fill(
            defaultAnnounce,
          )
          data = [...data, ...defaultAnnounces]
        }

        const announceDetails = shuffleArray(
          await Promise.all(
            data.map(async (announce: { id: string; userId: number }) => {
              if (!announce.id || announce.id === 'default') {
                return defaultAnnounce
              }

              const announceData = await fetchAnnounceDetails(announce.id)

              if (!announceData || !announceData.announce) {
                return defaultAnnounce
              }

              const user = userData.find(
                u => u.id === announceData.announce.userId,
              )
              const gender = user ? await getUserGender(user.id) : 'unknown'
              const filteredGallery = announceData.gallery?.filter(
                (img: { fileName: string }) =>
                  !img.fileName.startsWith('(verification-'),
              )

              return {
                ...announceData.announce,
                galleryUrl:
                  filteredGallery?.[0]?.fileUrl ||
                  'https://www.sexagogo.be/images/silouhette-female.jpg',
                user: {
                  nickname: user?.nickname || 'Unknown',
                  address: user?.address || { city: 'Unknown' },
                  gender,
                },
              }
            }),
          ),
        )

        setAnnounces(announceDetails)
      } catch (error) {
        console.error('❌ Erreur lors de la récupération des annonces:', error)
        setAnnounces(Array(7).fill(defaultAnnounce))
      }
    }

    fetchAllData()
  }, [])

  const settings = {
    dots: false,
    arrows: true,
    infinite: true,
    speed: 700,
    slidesToShow: 7,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: false,
    responsive: [
      {
        breakpoint: 1600,
        settings: {
          slidesToShow: 6,
          arrows: true,
        },
      },
      {
        breakpoint: 1370,
        settings: {
          slidesToShow: 5,
          arrows: true,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 4,
          arrows: true,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 3,
          arrows: false,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: false,
          centerMode: false,
          autoplay: false,
        },
      },
    ],
  }

  if (announces.length === 0) {
    return null
  }

  return (
    <div>
      <style>{sliderStyles}</style>

      <div className='w-full mx-auto relative pt-5 pb-0 bg-redpink text-white'>
        <div className='absolute w-full bg-anthracite bottom-0 block h-16'></div>
        <div className='slider-container'>
          <Slider {...settings} className='flex'>
            {Array.isArray(announces) && announces.length > 0 ? (
              announces.map((announce, index) => {
                if (!Array.isArray(announces) || announces.length === 0) {
                  return <p>🚨 Aucune annonce disponible</p>
                }

                const gender = formatGender(announce.user?.gender)
                const statuses = Array.isArray(announce.status)
                  ? formatMultipleStatuses(announce.status)
                  : 'unknown-status'
                const formattedURL = formatURL(
                  announce.title,
                  Number(announce.id),
                )

                const url = `/announce/${gender}/${statuses}/${formattedURL}`

                const handleClick = async (
                  event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
                ) => {
                  event.preventDefault()
                  try {
                    await viewAnnounce(Number(announce.id))
                    window.location.href = url
                  } catch (error) {
                    console.error('Error:', error)
                  }
                }

                return (
                  <div key={`${announce.id}-${index}`}>
                    <div className='slide-content'>
                      <Link to={url} onClick={handleClick}>
                        <div className='relative'></div>
                        <div className='profile-image-container'>
                          <img
                            src={announce.galleryUrl}
                            alt={
                              announce.isDefault
                                ? t('your-announce-here')
                                : `${t('profile-photo-of')} ${
                                    announce.user.nickname
                                  }`
                            }
                            className='profile-image'
                          />
                          <ImageWithApiKey
                            imageUrl={announce.galleryUrl}
                            apiKey={X_API_KEY}
                            height='100%'
                            className='profile-image'
                          />
                          {announce.isDefault && (
                            <div className='absolute inset-0 flex items-center justify-center'>
                              <span className='text-white px-8 text-lg text-center drop-shadow-[2px_2px_4px_rgba(0,0,0,0.5)]'>
                                {t('your-announce-here')}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className='info-container'>
                          <h3 className='text-lg text-redpink uppercase font-bold pt-0'>
                            {announce.user.nickname || 'nickname'}
                          </h3>
                          <p className='text-sm pb-2 p-0 m-0'>
                            {announce.user.address?.city || 'city'}
                          </p>
                        </div>
                      </Link>
                    </div>
                  </div>
                )
              })
            ) : (
              <p>🚨 Aucune annonce disponible</p>
            )}
          </Slider>
        </div>
      </div>
    </div>
  )
}

export default PremiumSlider
